import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit, Delete, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Appointment, AppointmentStatus } from '../types/entities';

type SortField = 'date' | 'client' | 'hairdresser' | 'status';
type SortDirection = 'asc' | 'desc' | 'default';

interface AppointmentsTableProps {
  appointments: Appointment[];
  onEdit: (appointment: Appointment) => void;
  onDelete: (appointment: Appointment) => void;
  onInfo: (appointment: Appointment) => void;
}

const AppointmentsTable: React.FC<AppointmentsTableProps> = ({
  appointments,
  onEdit,
  onDelete,
  onInfo,
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: default -> asc -> desc -> default
      if (sortDirection === 'default') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('default');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAppointments = [...appointments].sort((a, b) => {
    if (sortDirection === 'default' || !sortField) {
      return 0; // No sorting, maintain original order
    }

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'date':
        aValue = new Date(a.date);
        bValue = new Date(b.date);
        break;
      case 'client':
        aValue = a.client?.name?.toLowerCase() || '';
        bValue = b.client?.name?.toLowerCase() || '';
        break;
      case 'hairdresser':
        aValue = a.hairdresser?.name?.toLowerCase() || '';
        bValue = b.hairdresser?.name?.toLowerCase() || '';
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const formatDate = (date: Date): string => {
    // Handle UTC date from database and convert to local timezone
    const localDate = new Date(date);
    
    // Format as dd.mm.yyyy, hh:mm
    const day = String(localDate.getDate()).padStart(2, '0');
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const year = localDate.getFullYear();
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const formatPrice = (price: string): string => {
    const numPrice = Math.round(parseFloat(price));
    return `${numPrice} zł`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  };

  const getStatusColor = (status: AppointmentStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case AppointmentStatus.NEW:
        return 'primary';
      case AppointmentStatus.CONFIRMED:
        return 'default'; // Will use custom purple styling
      case AppointmentStatus.COMPLETED:
        return 'success';
      case AppointmentStatus.CANCELED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusChipSx = (status: AppointmentStatus) => {
    if (status === AppointmentStatus.CONFIRMED) {
      return {
        backgroundColor: '#9c27b0',
        color: 'white',
        fontSize: '0.75rem',
        '&:hover': {
          backgroundColor: '#8e24aa',
        }
      };
    }
    return { fontSize: '0.75rem' };
  };

  const getStatusLabel = (status: AppointmentStatus): string => {
    switch (status) {
      case AppointmentStatus.NEW:
        return t('appointments.new');
      case AppointmentStatus.CONFIRMED:
        return t('appointments.confirmed');
      case AppointmentStatus.COMPLETED:
        return t('appointments.completed');
      case AppointmentStatus.CANCELED:
        return t('appointments.canceled');
      default:
        return status;
    }
  };

  const calculateTotalPrice = (services: any[]): number => {
    return services.reduce((total, service) => total + parseFloat(service.price), 0);
  };

  const calculateTotalDuration = (services: any[]): number => {
    return services.reduce((total, service) => total + service.duration, 0);
  };

  const calculateFinishTime = (appointment: Appointment): string => {
    if (!appointment.date || !appointment.services || appointment.services.length === 0) {
      return '';
    }
    
    const startDate = new Date(appointment.date);
    const totalDurationMinutes = calculateTotalDuration(appointment.services);
    const finishDate = new Date(startDate.getTime() + totalDurationMinutes * 60000);
    
    return formatDate(finishDate);
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedAppointments.map((appointment) => (
          <Card key={appointment.id} sx={{ width: '100%' }}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {appointment.client?.name || t('calendar.unknownClient')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    color="info"
                    onClick={() => onInfo(appointment)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(appointment)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(appointment)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    <strong>{t('appointments.startTime')}:</strong> {formatDate(appointment.date)}
                  </Typography>
                  <Chip
                    label={getStatusLabel(appointment.status)}
                    size="small"
                    color={getStatusColor(appointment.status)}
                    variant="filled"
                    sx={getStatusChipSx(appointment.status)}
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  <strong>{t('appointments.finishTime')}:</strong> {calculateFinishTime(appointment) || t('appointments.notAvailable')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  <strong>{t('common.hairdresser')}:</strong> {appointment.hairdresser?.name || t('calendar.unknownHairdresser')}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    <strong>{t('appointments.totalPrice')}:</strong> {appointment.services ? formatPrice(calculateTotalPrice(appointment.services).toString()) : '0 zł'}
                  </Typography>
                  {appointment.services && appointment.services.length > 0 && (
                    <Chip
                      label={`${appointment.services.length} ${appointment.services.length !== 1 ? t('common.services') : t('common.service')}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'date'}
                direction={sortField === 'date' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('date')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('appointments.startTime')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('appointments.finishTime')}</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'client'}
                direction={sortField === 'client' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('client')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.client')}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'hairdresser'}
                direction={sortField === 'hairdresser' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('hairdresser')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.hairdresser')}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'status'}
                direction={sortField === 'status' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('status')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.status')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('common.price')}</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAppointments.map((appointment) => (
            <TableRow key={appointment.id} hover>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(appointment.date)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {calculateFinishTime(appointment) || 'Not available'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="medium">
                  {appointment.client?.name || 'Unknown'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {appointment.hairdresser?.name || 'Unknown'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusLabel(appointment.status)}
                  size="small"
                  color={getStatusColor(appointment.status)}
                  variant="filled"
                  sx={getStatusChipSx(appointment.status)}
                />
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="medium">
                  {appointment.services ? formatPrice(calculateTotalPrice(appointment.services).toString()) : '0 zł'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => onInfo(appointment)}
                    color="info"
                    title={t('appointments.viewDetails')}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(appointment)}
                    color="primary"
                    title={t('common.edit')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(appointment)}
                    color="error"
                    title={t('common.delete')}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppointmentsTable;
